import _ from 'lodash';
import api from '../../api';
import environment from '../../environment';
import layers from '../../layers';
import filters from '../filters';
import { customScrollbar, formatAddressResults } from '../../shared';
import { notify, NOTIFY_TYPE } from '../../shared/notify';
import infoBoard from '../infoboard';
import map from '../map';
import streetView from '../map/streetview';
import templates from '../templates';
import translate from '../../core/tools/translate';
import wellknown from 'wellknown';
import AuthService from '../../../services/authservice';
import { FeaturePermission } from '../../../constants/feature.permission.helper';
import Workspace from '../../workspaces';
import { ControlType, Item } from '../../workspaces/item';
import { ContextEnum } from '../../../enuns/context.enum';
import geojsonArea from '@mapbox/geojson-area';
import { SearchTypeEnum } from '../../../enuns/searchType.enum';

const LayerControls = name => require('../../controls').get(name);
const LayerInfo = name => require('../../info').get(name);

const isAuthorized = permission => api.authorization.isAuthorized(permission);

// O check "limitar a area pesquisada" das camadas de loja pos-venda depende de
// LOCALE.FILTERED, entao precisa ser reavaliado sempre que a busca muda.
const _refreshAfterSalesSearchedAreaFilter = () => {
    ['NetworkAfterSales', 'CompetitorsAfterSales'].forEach(name => {
        try {
            LayerControls(name).refreshSearchedAreaFilter();
        } catch (error) {
            console.log(`WARNING: ${name} control not available`);
        }
    });
};

const search = (() => {
    let isActive = false;
    let googleSearch;
    let dbSearchClientAreas;
    let dbSearchOfficialAreas;
    let tollSearch;
    let params;
    let $searchbox;
    let $clearInputSearch;

    const _invalidUserLogout = () => {
        let err;
        try {
            AuthService.signinPopup();
        } catch (_error) {
            err = _error;
            console.log(`ERROR: ${err.message}`);
        }
    };

    const _setRequestHeader = request =>
        _.forEach(AuthService.getAccessTokenHeader(), (value, key) => request.setRequestHeader(key, value));

    const _changeFilterUrl = (ids, type) => {
        if (type) {
            params = [];
            params.push({
                IDs: ids,
                Tipo: type
            });
        } else {
            params = null;
        }
        return params;
    };

    const _hasFitBoundAopAndPma = (layer) => {
        try {
            const removeAccents = (str) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

            let featureAopOrPma = layer.toGeoJSON().features.filter(x =>
                removeAccents(x.properties.Tipo) !== 'regionais'
                && removeAccents(x.properties.Tipo) !== 'regional'
                && removeAccents(x.properties.Tipo) !== 'divisao'
                && removeAccents(x.properties.Tipo) !== 'zona');

            const getGreaterAreaCoordinate = (features) => {
                const coordinatesArea = [];

                features.geometry.coordinates.map(coordinate => {
                    coordinatesArea.push({ coordinate, area: geojsonArea.geometry({ ...features.geometry, coordinates: [coordinate] }) });
                });

                return _.maxBy(coordinatesArea, 'area').coordinate;
            };

            if (featureAopOrPma.length > 0) {
                featureAopOrPma = {
                    ...featureAopOrPma[0],
                    geometry: {
                        ...featureAopOrPma[0].geometry,
                        coordinates: [getGreaterAreaCoordinate(featureAopOrPma[0])]
                    }
                };
                const l = L.geoJson(featureAopOrPma);
                map.getCurrent().fitBounds(l.getBounds());
                return true;
            }

            return false;
        } catch (err) {
            return false;
        }
    };

    const _goToLocation = (values, callback) => {
        environment.set('LOCALE.FILTERED', values);
        const l = layers.get('Location');
        const selectedAreasLayer = layers.get('SelectedAreas');
        const markedAreasLayer = layers.get('MarkedArea');

        if (selectedAreasLayer.state.visible)
            selectedAreasLayer.redraw();

        if (markedAreasLayer.state.visible)
            markedAreasLayer.redraw();

        const done = () => {
            l.setOptions({ done: null });

            if (!_hasFitBoundAopAndPma(l)) {
                map.getCurrent().fitBounds(l.layer.getBounds());
            }

            _refreshAfterSalesSearchedAreaFilter();

            if (callback) {
                callback();
            }
        };

        l.setOptions({
            remote: {
                params: {
                    id: values.id,
                    idTipoLocalidade: values.type,
                    agrupamento: values.grouping
                }
            },
            done
        });

        l.redraw();
    };

    const _highlightLocation = (latlng, zoom = 14) => {
        streetView.locate(latlng);
        map.get().setView(latlng, zoom, false);
    };

    const _loadData = () => {
        const isAfterSales = environment.get('CONFIG.IS_AFTER_SALES');

        googleSearch = new Bloodhound({
            datumTokenizer(d) {
                return Bloodhound.tokenizers.whitespace(d.value);
            },
            queryTokenizer: Bloodhound.tokenizers.whitespace,
            limit: 20,
            remote: {
                url: 'query=%QUERY',
                wildcard: '%QUERY',
                ajax: {
                    statusCode: {
                        401: _invalidUserLogout
                    },
                    beforeSend(request, settings) {
                        let latlng, param, re;
                        _setRequestHeader(request);
                        re = /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),\s*[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/;
                        param = decodeURIComponent(settings.url).split('query=')[1];
                        if (re.test(param)) {
                            params = param.split(',');
                            latlng = new L.LatLng(params[0], params[1]);
                            _highlightLocation(latlng);
                            request.abort();
                        } else {
                            settings.url = `${environment.get('API_HOST')}/general/geocode?address=${param}&country=${window.NeoGIG.country}`;
                        }
                    }
                },
                filter(data) {
                    let requestList;
                    data = JSON.parse(data);
                    requestList = [];
                    if (data) {
                        data.results.forEach(entry => {
                            return requestList.push({
                                name: entry.formatted_address,
                                value: entry.formatted_address,
                                label: translate('Common.Address'),
                                data: formatAddressResults(entry),
                                action: 1
                            });
                        });
                    }
                    return requestList;
                }
            }
        });
        googleSearch.initialize();

        let urlLocationClient = `${environment.get('API_HOST')}/localidade/buscarClient`;

        if (isAfterSales) {
            urlLocationClient = `${environment.get('API_HOST')}/aftersales/locales/client`;
        }

        dbSearchClientAreas = new Bloodhound({
            datumTokenizer(d) {
                return Bloodhound.tokenizers.whitespace(d.value);
            },
            queryTokenizer: Bloodhound.tokenizers.whitespace,
            limit: 300,
            remote: {
                url: urlLocationClient,
                replace(url, uriEncodedQuery) {
                    return `${url}#${uriEncodedQuery}`;
                },
                cache: false,
                ajax: {
                    statusCode: {
                        401: _invalidUserLogout
                    },
                    type: 'POST',
                    dataType: 'json',
                    contentType: 'application/json; charset=utf-8',
                    beforeSend(request, settings) {
                        _setRequestHeader(request);
                        $('#search-input-icon').html('<i class="fa fa-circle-o-notch fa-spin"></i>');
                        settings.data = JSON.stringify({
                            consulta: $searchbox.val(),
                            type: SearchTypeEnum.ClientAreas,
                            idVehicleType: environment.get('CONFIG.USERVEHICLETYPE'),
                            filtros: params
                        });
                    },
                    complete() {
                        return $('#search-input-icon').html('<i class="fa fa-search"></i>');
                    }
                },
                filter(data) {
                    let requestList;
                    requestList = [];
                    if (data) {
                        data.forEach(entry => {
                            let requestItem;
                            if (String(entry.tipo).trim() === 'Agrupamento') {
                                entry.nome = entry.nome.toCapitalCase();
                            }
                            requestItem = {
                                action: 3,
                                areaId: entry.idAgrupamento,
                                id: entry.id,
                                name: entry.complemento ? entry.complemento : entry.nome || '',
                                type: typeof entry.idTipo === 'number' ? entry.idTipo : entry.locationType,
                                label: entry.tipo,
                                grouping: entry.agrupamento,
                                value: entry.complemento ? entry.complemento : entry.nome || ''
                            };
                            return requestList.push(requestItem);
                        });
                    }
                    setTimeout(() => customScrollbar($('.tt-suggestions')), 300);
                    return requestList;
                }
            }
        });
        dbSearchClientAreas.initialize();

        let urlLocationOfficial = `${environment.get('API_HOST')}/localidade/buscarOfficial`;

        if (isAfterSales) {
          urlLocationOfficial = `${environment.get('API_HOST')}/aftersales/locales/official`;
        }

        dbSearchOfficialAreas = new Bloodhound({
            datumTokenizer(d) {
                return Bloodhound.tokenizers.whitespace(d.value);
            },
            queryTokenizer: Bloodhound.tokenizers.whitespace,
            limit: 300,
            remote: {
                url: urlLocationOfficial,
                replace(url, uriEncodedQuery) {
                    return `${url}#${uriEncodedQuery}`;
                },
                cache: false,
                ajax: {
                    statusCode: {
                        401: _invalidUserLogout
                    },
                    type: 'POST',
                    dataType: 'json',
                    contentType: 'application/json; charset=utf-8',
                    beforeSend(request, settings) {
                        _setRequestHeader(request);
                        $('#search-input-icon').html('<i class="fa fa-circle-o-notch fa-spin"></i>');
                        settings.data = JSON.stringify({
                            consulta: $searchbox.val(),
                            type: SearchTypeEnum.OfficialAreas,
                            idVehicleType: environment.get('CONFIG.USERVEHICLETYPE'),
                            filtros: params
                        });
                    },
                    complete() {
                        return $('#search-input-icon').html('<i class="fa fa-search"></i>');
                    }
                },
                filter(data) {
                    let requestList;
                    requestList = [];
                    if (data) {
                        data.forEach(entry => {
                            let requestItem;
                            if (String(entry.tipo).trim() === 'Agrupamento') {
                                entry.nome = entry.nome.toCapitalCase();
                            }
                            requestItem = {
                                action: 3,
                                areaId: entry.idAgrupamento,
                                id: entry.id,
                                name: entry.complemento ? entry.complemento : entry.nome || '',
                                type: typeof entry.idTipo === 'number' ? entry.idTipo : entry.locationType,
                                label: entry.tipo,
                                grouping: entry.agrupamento,
                                value: entry.complemento ? entry.complemento : entry.nome || ''
                            };
                            return requestList.push(requestItem);
                        });
                    }
                    setTimeout(() => customScrollbar($('.tt-suggestions')), 300);
                    return requestList;
                }
            }
        });
        dbSearchOfficialAreas.initialize();

        tollSearch = new Bloodhound({
            datumTokenizer({ value }) {
                return Bloodhound.tokenizers.whitespace(value);
            },
            queryTokenizer: Bloodhound.tokenizers.whitespace,
            limit: 150,
            remote: {
                url: `${environment.get('API_HOST')}/Layer/Search/Toll/`,
                replace(url, uriEncodedQuery) {
                    return `${url}#${uriEncodedQuery}`;
                },
                cache: false,
                ajax: {
                    statusCode: {
                        401: _invalidUserLogout
                    },
                    type: 'GET',
                    beforeSend(request, settings) {
                        _setRequestHeader(request);
                        settings.url += $searchbox.val();
                    }
                },
                filter(data) {
                    if (!data)
                        return [];

                    return data.map(x => ({
                        label: translate('Common.Toll'),
                        name: `${x.nameToll}, ${x.nameHighway}`,
                        value: `${x.nameToll}, ${x.nameHighway}`,
                        id: x.id,
                        geometry: x.shape.geometry,
                        action: 5
                    }));
                }
            }
        });

        tollSearch.initialize();
    };

    const _activeTypeahead = () => {
        if (isActive) {
            $($searchbox).typeahead('destroy');
        }

        const options = {
            highlight: false,
            hint: false,
            minLength: 3
        };

        const dbSearchClientAreasOptions = {
            name: 'locationClient',
            displayKey: 'value',
            source: dbSearchClientAreas.ttAdapter(),
            templates: {
                header: '<p class="result-title">Client Location</p>',
                suggestion: templates.get('search-result')
            }
        };

        const dbSearchOfficialAreasOptions = {
            name: 'locationOfficial',
            displayKey: 'value',
            source: dbSearchOfficialAreas.ttAdapter(),
            templates: {
                header: '<p class="result-title">Official Location</p>',
                suggestion: templates.get('search-result')
            }
        };

        const googleSearchOptions = {
            name: 'points',
            displayKey: 'value',
            source: googleSearch.ttAdapter(),
            templates: {
                header: '<p class="result-title">Points</p>',
                suggestion: templates.get('search-result')
            }
        };

        const tollSearchOptions = {
            name: 'tolls',
            displayKey: 'value',
            source: tollSearch.ttAdapter(),
            templates: {
                header: '<p class="result-title">Tolls</p>',
                suggestion: templates.get('search-result')
            }
        };

        const dic: { authorized: boolean, options: any }[] = [
            { authorized: true, options: googleSearchOptions },
            { authorized: isAuthorized(FeaturePermission.SYSTEM.search), options: dbSearchClientAreasOptions },
            { authorized: isAuthorized(FeaturePermission.SYSTEM.search), options: dbSearchOfficialAreasOptions },
            { authorized: isAuthorized(FeaturePermission.LAYER_GENERAL.tollEnable), options: tollSearchOptions },
        ];

        return $($searchbox).typeahead(options, dic.filter(x => x.authorized).map(x => x.options));
    };

    const _updateFiltersValues = (id, name) => {
        const currentMap = map.getCurrent();

        const filterValues = _.cloneDeep(filters.get(`${_.kebabCase(id)}-filter-id`, currentMap._id));

        const values =
            _.size(filterValues) && filterValues.group
                ? filterValues
                : _.assign(
                    {},
                    {
                        group: {
                            metadata: {
                                name: 'group',
                                text: 'value',
                                value: 'value'
                            },
                            text: [],
                            value: []
                        }
                    },
                    filterValues
                );

        const filteredStores = _.filter(
            environment.get('CONFIG.FILTERS.DEALERS_STORES'),
            s => s.Grupo.trim().toUpperCase() === name.trim().toUpperCase()
        );
        values.group.text = _(filteredStores)
            .map(s => s.Grupo)
            .uniq()
            .value();
        values.group.value = _(filteredStores)
            .map(s => s.Grupo)
            .uniq()
            .value();

        filters.set(`${_.kebabCase(id)}-filter-id`, currentMap._id, values);
    };

    const _handleFuelStationChosen = (itemChosen) => {
        const point = wellknown.parse(itemChosen.geometry.wellKnownText);
        const [lng, lat] = point.coordinates;

        const l = layers.get('FuelStation');
        LayerControls('FuelStation').check(false);
        LayerControls('FuelStation').check(true); //dont know why, but this is the only way works

        _highlightLocation({ lat, lng }, 16);

        l.reload();
        l.redraw();
    };

    const _handleTollChosen = (itemChosen) => {
        const point = wellknown.parse(itemChosen.geometry.wellKnownText);
        const [lng, lat] = point.coordinates;

        if (!LayerControls('Toll').check())
            LayerControls('Toll').check(true);

        _highlightLocation({ lat, lng });
    };

    const _onSelect = () => {
        $($searchbox)
            .closest('form')
            .on('submit', e => e.preventDefault());
        $($searchbox).on('focus', function () {
            return setTimeout((_this => () => $(_this).select())(this), 100);
        });

        $($searchbox).on('input change', function (e) {
            e.preventDefault();
            if ($(this).val().length === 0) {
                const selectedAreasLayer = layers.get('SelectedAreas');
                const markedAreasLayer = layers.get('MarkedArea');

                if (selectedAreasLayer.state.visible)
                    selectedAreasLayer.redraw();

                if (markedAreasLayer.state.visible)
                    markedAreasLayer.redraw();

                environment.set('LOCALE.FILTERED', undefined);
                _refreshAfterSalesSearchedAreaFilter();
                layers.hide('Location');
                layers.clear();
                infoBoard.close();
                LayerInfo('Summary').create("");
            } else {
                $clearInputSearch.show();
            }
        });

        $($searchbox).on('typeahead:selected', (obj, options) => {
            Workspace.addAction(new Item(`#${$searchbox.prop('id')}`, { ...options }, ControlType.TypeaHead));

            if (options.action === 4) {
                _handleFuelStationChosen(options);
                return;
            }

            if (options.action === 5) {
                _handleTollChosen(options);
                return;
            }

            let _callback, _setFilter, latlng, type;
            api.general.log.action(ContextEnum.PanelSuperior, `Busca - ${options.label.trim()}`, JSON.stringify(options));
            switch (options.action) {
                case 1:
                    latlng = new L.LatLng(options.data.coordinates.lat, options.data.coordinates.lng);
                    return _highlightLocation(latlng);
                default:
                    type = options.label.trim().toUpperCase();
                    if (type === 'PONTO DE LOJA') {
                        _callback = () => {
                            layers.hide('Location');
                            LayerControls('Dealerships').check(true);
                            LayerControls('Competitors').check(true);
                            LayerControls('StoreInactive').check(true);
                        };
                        _goToLocation(options, _callback);
                    }
                    if (type === 'GRUPO') {
                        _setFilter = layer => {
                            let values;
                            values = {};
                            _.forEach(layers.get(layer).options.filter, (value, key) => {
                                values[key] = value;
                            });

                            if (!values.properties) {
                                values.properties = {};
                            }

                            values.properties['Grupo'] = options.name;
                            layers.setOptions(layer, {
                                filter: values
                            });

                            _updateFiltersValues(layer, options.name);
                        };
                        _setFilter('Dealerships');

                        if (LayerControls('Dealerships').check()) {
                            layers.reload('Dealerships');
                        } else {
                            LayerControls('Dealerships').check(true);
                        }
                        _setFilter('Competitors');

                        if (LayerControls('Competitors').check()) {
                            layers.reload('Competitors');
                        } else {
                            LayerControls('Competitors').check(true);
                        }

                        _setFilter('StoreInactive');

                        if (LayerControls('StoreInactive').check()) {
                            layers.reload('StoreInactive');
                        } else {
                            LayerControls('StoreInactive').check(true);
                        }
                    } else {
                        _goToLocation(options);

                        if (isAuthorized(FeaturePermission.INFO_SUMMARY.show)) {
                            infoBoard.open();
                            options.context = 'area-selection';
                            options.visible = true;
                            LayerInfo('Summary').create(options);
                        }

                        if (isAuthorized(FeaturePermission.AFTER_SALES_SUMMARY_INFO.show)) {
                            const marketArea = Object.keys(environment.get('CONFIG.FILTERS.MARKED_AREA_TYPES'))
                                .map(x => environment.get('CONFIG.FILTERS.MARKED_AREA_TYPES')[x].id);

                            const areas = Object.keys(environment.get('CONFIG.FILTERS.AREAS_TYPES'))
                                .map(x => environment.get('CONFIG.FILTERS.AREAS_TYPES')[x].id);

                            if (areas.concat(marketArea).includes(options.type)) {
                                options.context = 'after-sales-area-selection';
                                options.visible = true;
                                LayerInfo('SummaryAfterSales').create(options);
                            } else {
                                infoBoard.close();
                            }
                        }
                    }
            }
        });

        $($clearInputSearch).on('click', () => {
            if (!!$searchbox.val().length) {
                streetView.clearPin();
            }
            streetView.clearLocate();
            $clearInputSearch.hide();
            $($searchbox).typeahead('val', '');
            $($searchbox).trigger('input');
            environment.set('LOCALE.FILTERED', undefined);
            _refreshAfterSalesSearchedAreaFilter();
        });
    };

    return (() => {
        async function Search() {
            if (await AuthService.isAuthenticated()) {
                if (!isActive) {
                    const values = {
                        list: _.map(environment.get('CONFIG.FILTERS.FILTERBY'), (group, index) => {
                            const option = {
                                size: 12 / environment.get('CONFIG.FILTERS.FILTERBY').length,
                                type: group.type,
                                ids: [],
                                values: []
                            };
                            _.forEach(group.options, value => {
                                option.ids.push(value.id);
                                option.values.push({
                                    id: value.id,
                                    type: group.type,
                                    name: value.name
                                });
                            });
                            if (index === environment.get('CONFIG.FILTERS.FILTERBY').length - 1) {
                                option.all = 'Tudo';
                            }
                            return option;
                        })
                    };

                    const $container = $(templates('search-input', values));
                    const $searchFilterInput = $container.find('.search-panel .dropdown-menu');
                    const searchFilterContainer = $container.find('.dropdown-menu-large');
                    $searchbox = $container.find('input');
                    $clearInputSearch = $container.find('#clear-search');

                    $container.appendTo($('#navbar-search'));
                    searchFilterContainer.width(180 * environment.get('CONFIG.FILTERS.FILTERBY').length);
                    $searchFilterInput.find('a').click(function (e) {
                        e.preventDefault();
                        _changeFilterUrl(String($(this).data('id')).split(','), $(this).data('type'));
                        $('.search-panel span#search_concept').text($(this).text());
                    });
                    isActive = true;
                }
                _loadData();
                _activeTypeahead();
                _onSelect();
            } else {
                notify(NOTIFY_TYPE.ERROR, 'Usuário não autenticado');
            }
        }

        return Search;
    })();
})();

export default search;



// WEBPACK FOOTER //
// ./src/scripts/core/search/index.js